import React, { useState, useEffect } from 'react'
import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import {
  faCircleXmark,
  faPhoneVolume,
  faBrain,
  faNotesMedical,
  faHeartbeat,
  faStethoscope,
  faChild,
  faChalkboardTeacher,
  faChevronRight,
  faSpa,
  faCirclePlay,
  faShareNodes,
  faDownload,
} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'

const EditAppointmentSlideout = ({ open, setOpen, booking }) => {
  const { id } = useParams()
  const { setShowLoader, createInfo, auth } = useAuth()
  const navigate = useNavigate()
  const axios = useAxiosPrivate()
  const [referral, setReferral] = useState({})

  useEffect(() => {
    getBooking()
  }, [])

  const getBooking = async () => {
    setShowLoader(true)
    try {
      const res = await axios.get(
        `/bookings/user/${auth?.user?.id}/booking/${booking}`
      )
      let { data, included } = res.data.result

      // Ensure they're arrays
      data = Array.isArray(data) ? data : [data]
      included = Array.isArray(included) ? included : [included]

      const combined = data.map((item, index) => ({
        data: item,
        included: included[index] || null,
      }))
      setReferral(combined)
      setShowLoader(false)
    } catch (error) {
      console.error('Error fetching bookings:', error)
    }
  }

  function formatDate(datetimeStr, type = 'date') {
    const m = moment(datetimeStr)

    if (type === 'date') {
      // moment's "Do" format token includes ordinal (e.g., 1st, 2nd, 3rd)
      return m.format('dddd Do MMMM YYYY')
    }

    if (type === 'time') {
      // 12-hour format with AM/PM, zero-padded hour and minute
      return m.format('hh:mm A')
    }

    return '' // fallback if type is unknown
  }
  return (
    <form className="w-full max-w-3xl mx-auto bg-[#f8f6fb] shadow-xl p-6 rounded-lg overflow-y-scroll">
      {/* Header Section */}
      <div className="px-4 py-6 sm:px-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="bg-[#979EF4] px-4 py-3 rounded-xl">
              <FontAwesomeIcon
                icon={faPhoneVolume}
                className="text-white text-xl"
              />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-[#41264B]">
                GP Appointment
              </h2>
              <p className="text-sm text-gray-600">
                Get faster support from clinicians for minor illness or
                non-urgent conditions.
              </p>
            </div>
          </div>
          <div className="ml-3 flex h-7 items-center">
            <button
              type="button"
              className="relative text-[#41264B] hover:text-nav-dark focus:outline-none focus:ring-2 focus:ring-nav"
              onClick={() => console.info('Form closed')}
            >
              <span className="sr-only">Close panel</span>
              <FontAwesomeIcon icon={faCircleXmark} className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      <section className="p-6 flex flex-col gap-6 h-full text-portal-dark-blue">
        {/* Booking Summary Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b">
                <td className="px-4 py-3 font-semibold">Date and time</td>
                <td className="px-4 py-3">
                  {formatDate(referral[0]?.included?.attributes?.start, 'date')}{' '}
                  {formatDate(referral[0]?.included?.attributes?.start, 'time')}
                </td>
              </tr>
              <tr className="border-b">
                <td className="px-4 py-3 font-semibold">Patient</td>
                <td className="px-4 py-3">{auth?.user?.name}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Reason for booking */}
        <div>
          <h2 className="text-lg font-semibold mb-2">Reason for booking</h2>
          <label className="block text-sm text-gray-600 mb-1">
            Symptoms of concern
          </label>
          <textarea
            rows={5}
            value={referral[0]?.data?.attributes?.reasonForBooking}
            className="w-full border rounded-md p-3 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Please briefly describe your reason for booking and symptoms of concern."
          />
        </div>

        {/* Documents */}
        <div>
          <h2 className="text-lg font-semibold mb-2">
            Consultation notes and documents
          </h2>
          <p className="text-sm text-gray-600">
            There are no documents available yet. You’ll receive an email when
            they are available for download.
          </p>
        </div>

        {/* Contact */}
        <div>
          <h2 className="text-lg font-semibold mb-2">
            Need to speak to someone?
          </h2>
          <p className="text-sm text-gray-700 mb-1">
            Talk to our customer service team on:
          </p>
          <p className="text-sm font-semibold underline text-portal-medium mb-4">
            {/* 01773 814403 */}
            0123 456 7890
          </p>
          <p className="text-sm text-gray-700 mb-1">Or email us at:</p>
          <p className="text-sm font-semibold underline text-portal-medium pb-4">
            {/* nurse@uk-sas.co.uk */}
            gppatient@healthhero.com.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-4 w-full pt-2">
          <button
            type="submit"
            className="w-1/2 bg-white text-black border border-gray-300 rounded-md py-2 hover:bg-gray-100 transition"
            onClick={() => console.info('Edit clicked')}
          >
            Edit
          </button>
          <button
            type="button"
            className="w-1/2 bg-yellow-400 text-black rounded-md py-2 hover:bg-yellow-300 transition"
            onClick={() => console.info('Cancel clicked')}
          >
            Cancel
          </button>
        </div>
      </section>
    </form>
  )
}

export default EditAppointmentSlideout
